import {
  Image,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
} from "@heroui/react";
import { SupportedGames } from "@/types/game";
import { getGameHandler } from "@/games";
import CardTile from "@/components/Card/CardTile";
import { orientationOf } from "@/lib/cardArt";
import { formatPrice } from "@/lib/moneyHelper";
import { usePriceManager } from "@/stores/priceManager";
import { priceManager } from "@/lib/priceManager";
import { getTCGPlayerUrl } from "@/lib/tcgplayer/tcgplayerAffiliateLink";
import { useEffect } from "react";

export default function DecklistCard({
  gameName,
  cardId,
  count,
  onPress,
  classNames,
  imgUrlOverride,
  showPrice = true,
}: {
  gameName: SupportedGames;
  cardId: string;
  count?: string;
  onPress: any;
  classNames: string;
  imgUrlOverride?: string;
  showPrice?: boolean;
}) {
  const gameHandler = getGameHandler(gameName);
  let imgUrl = gameHandler.getCardImage(cardId, "316x440", "");
  if (imgUrlOverride) {
    imgUrl = imgUrlOverride;
  }

  // Subscribe to both the prices and the fetch function
  const prices = usePriceManager((state) => state.prices[gameName]);
  const fetchPrices = usePriceManager((state) => state.fetchPrices);

  // Fetch prices when component mounts
  useEffect(() => {
    if (showPrice) {
      fetchPrices(gameName);
    }
  }, [gameName, showPrice, fetchPrices]);

  // Use priceManager singleton for ID mapping
  const tcgplayerId = priceManager.getTCGPlayerProductId(gameName, cardId);
  const cardData = gameHandler.getCardData(cardId);
  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";
  const {
    isOpen: isDebugOpen,
    onOpen: onDebugOpen,
    onClose: onDebugClose,
  } = useDisclosure();

  let priceDiv = <></>;
  let debugDiv = <></>;
  let debugModal = <></>;

  // Debug modal and button for localhost
  if (isLocalhost) {
    const debugInfo = {
      cardId,
      gameName,
      tcgplayerId: tcgplayerId || "NONE",
      "cardData.tcgplayer_id": cardData?.tcgplayer_id || "NONE",
      "cardData.name": cardData?.name,
      "cardData.card_number":
        (cardData as any)?.card_number || (cardData as any)?.number || "N/A",
      priceData: prices?.[tcgplayerId || ""] || "NO PRICE DATA",
      fullCardData: cardData,
    };

    debugDiv = (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDebugOpen();
        }}
        className="z-[22] absolute top-0 right-0 p-1 m-1 bg-blue-500/70 hover:bg-blue-600 text-white text-[10px] rounded"
      >
        🐛
      </button>
    );

    debugModal = (
      <Modal isOpen={isDebugOpen} onClose={onDebugClose} size="2xl">
        <ModalContent>
          <ModalHeader>Card Debug Info: {cardId}</ModalHeader>
          <ModalBody>
            <pre className="text-xs overflow-auto p-4 bg-slate-100 dark:bg-slate-900 rounded whitespace-pre-wrap break-all">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(debugInfo, null, 2),
                );
                alert("Copied to clipboard!");
              }}
              className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Copy to Clipboard
            </button>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (tcgplayerId && showPrice) {
    let cardPrice: number | null = null;
    if (tcgplayerId && prices && prices[tcgplayerId]) {
      const priceData = prices[tcgplayerId];
      // Use low price if available, otherwise fall back to market price
      const priceString =
        priceData["l"] && priceData["l"].trim() !== ""
          ? priceData["l"]
          : priceData["m"];
      cardPrice =
        priceString && priceString.trim() !== ""
          ? parseFloat(priceString)
          : null;
    }
    if (cardPrice !== null && !isNaN(cardPrice)) {
      priceDiv = (
        <Link
          isExternal
          className="group z-[21] absolute text-[11px] text-slate-300 bottom-0 left-0 p-1 sm:text-xs rounded m-1 bg-black/70 hover:bg-black/100 hover:opacity-100 transition-all duration-300 flex items-center justify-center"
          href={getTCGPlayerUrl(parseInt(tcgplayerId), "decklist-card")}
        >
          <div className="flex items-center justify-center transition-all duration-300">
            <div className="group-hover:text-white">
              {formatPrice(cardPrice)}
            </div>
            <div className="ml-1 mb-[1px] opacity-0 group-hover:opacity-100 transition-all duration-300 w-0 group-hover:w-3 overflow-hidden">
              <Image
                src="/logo/tcgplayer.png"
                alt="TCGplayer"
                width={12}
                height={12}
                radius="none"
              />
            </div>
          </div>
        </Link>
      );
    }
  }

  // A sideways card lies down in a decklist too, the same as in the grid it was
  // picked from. `imgUrlOverride` is a DON card, which is always portrait.
  const orientation = imgUrlOverride
    ? "portrait"
    : orientationOf(gameName, cardId);

  return (
    <>
      {debugModal}
      <div className={classNames}>
        <CardTile
          src={imgUrl.replaceAll("avif", "webp")}
          alt={cardId}
          orientation={orientation}
          caption={count ? count + "x" : undefined}
          onClick={onPress}
          radiusClassName="rounded-sm"
          wrapperClassName="m-1 cursor-pointer"
        >
          {priceDiv}
          {debugDiv}
        </CardTile>
      </div>
    </>
  );
}
